import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { orientedFace, planPoint, triangulateRing } from '../solids';
import { offsetPolyline, strokeRing } from '../planGeometry';
import type { WaterTest } from './moat';

/**
 * The brick wall of 1481-1494 (STYLE §8.2): a shield wall 1.40 m thick (AAR 45) whose top rises as
 * a parapet over a walk carried on round arches along its city side, "a wide walk on brick arches"
 * (Amsterdam city archaeology, 2017). The arches are those of the one measured wall of the same
 * height, Maastricht's first wall: piers 1.4 m wide joined by round arches 3.8 m wide and 3.5 m high,
 * over niches about 1.5 m deep. The painting and the woodcut of 1544 draw the walk open, with no
 * roof over it.
 */
export const WALL_ARCADE = {
  parapetHeight: 1.5,
  parapetThickness: 0.6,
  /** The arch ring and the walk's paving over the crown of each arch. */
  crownBelowWalk: 0.5,
  pier: 1.4,
  opening: 3.8,
  /** Bays are fitted to each straight run of the wall, so an opening varies within these bounds. */
  minOpening: 2.4,
  maxOpening: 5.0,
  niche: 1.5,
  /** The back of a niche stands this far in front of the shield wall, so the two never share a plane. */
  lining: 0.2,
  /** Lowest springing of an arch: the niches held dwellings (the city let them to the poor). */
  minSpringing: 1.0,
  archSegments: 8,
} as const;

/* A wall stands 0.1 m from its moat (tools/layout/fortifications.py); probes reach past that. */
const SIDE_PROBES = [0.5, 2, 4] as const;
const SIDE_PROBE_STEP = 2;

export interface WallSection {
  /** Height of the walk. */
  readonly walk: number;
  /** Height of the crown of every arch. */
  readonly crown: number;
  /** How far the arcade reaches behind the shield wall's inner face. */
  readonly depth: number;
}

/** The section of a wall `height` m high, or null when the widest arch would spring too low. */
export function wallSection(height: number): WallSection | null {
  const walk = height - WALL_ARCADE.parapetHeight;
  const crown = walk - WALL_ARCADE.crownBelowWalk;
  if (crown - WALL_ARCADE.maxOpening / 2 < WALL_ARCADE.minSpringing) return null;
  return { walk, crown, depth: WALL_ARCADE.niche + WALL_ARCADE.lining };
}

export interface ArchOpening {
  readonly from: number;
  readonly to: number;
}

/**
 * Whole bays along a straight stretch `span` m long, half a pier at either end: the count closest
 * to the Maastricht bay whose openings stay within bounds, or none.
 */
export function archOpenings(span: number): ArchOpening[] {
  const { pier, opening, minOpening, maxOpening } = WALL_ARCADE;
  const least = Math.max(1, Math.ceil(span / (maxOpening + pier)));
  const most = Math.floor(span / (minOpening + pier));
  if (most < least) return [];
  const count = Math.min(most, Math.max(least, Math.round(span / (opening + pier))));
  const bay = span / count;
  return Array.from({ length: count }, (_, i) => ({
    from: i * bay + pier / 2,
    to: (i + 1) * bay - pier / 2,
  }));
}

/** +1 when the city side, the one without the moat, lies left of the path; -1 when right. */
export function citySide(path: readonly Vec2[], thickness: number, isWater: WaterTest): 1 | -1 {
  let left = 0;
  let right = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]!;
    const b = path[i + 1]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (len === 0) continue;
    const n: Vec2 = [-(b[1] - a[1]) / len, (b[0] - a[0]) / len];
    for (let s = SIDE_PROBE_STEP / 2; s < len; s += SIDE_PROBE_STEP) {
      const p: Vec2 = [a[0] + ((b[0] - a[0]) * s) / len, a[1] + ((b[1] - a[1]) * s) / len];
      for (const probe of SIDE_PROBES) {
        const d = thickness / 2 + probe;
        if (isWater([p[0] + n[0] * d, p[1] + n[1] * d])) left++;
        if (isWater([p[0] - n[0] * d, p[1] - n[1] * d])) right++;
      }
    }
  }
  return left > right ? -1 : 1;
}

export interface WallStyles {
  readonly wall: VertexStyle;
  readonly niche: VertexStyle;
}

export interface WallPiece {
  readonly points: readonly Vec2[];
  readonly thickness: number;
  readonly height: number;
  readonly side: 1 | -1;
}

/** One straight run of the arcade: a point `s` along it and `a` across it, toward the city. */
interface Run {
  readonly origin: Vec2;
  readonly along: Vec2;
  readonly across: Vec2;
}

function runAt(run: Run, s: number, a: number): Vec2 {
  return [
    run.origin[0] + run.along[0] * s + run.across[0] * a,
    run.origin[1] + run.along[1] * s + run.across[1] * a,
  ];
}

function point3(p: Vec2, y: number): Vec3 {
  return planPoint(p[0], y, p[1]);
}

function planVector(v: Vec2, y = 0): Vec3 {
  return [v[0], y, -v[1]];
}

function coordinate(run: Run, p: Vec2): number {
  return (p[0] - run.origin[0]) * run.along[0] + (p[1] - run.origin[1]) * run.along[1];
}

interface RunFaces {
  readonly builder: MeshBuilder;
  readonly styles: WallStyles;
  readonly frame: Frame;
  readonly run: Run;
  readonly section: WallSection;
  readonly nicheBack: number;
  readonly front: number;
}

function arcPoint(o: ArchOpening, springing: number, k: number): Vec2 {
  const r = (o.to - o.from) / 2;
  const angle = (Math.PI * k) / WALL_ARCADE.archSegments;
  return [(o.from + o.to) / 2 + r * Math.cos(angle), springing + r * Math.sin(angle)];
}

/**
 * The niche under one arch: its back, its sides and the arch's soffit. The painting draws each
 * opening as one dark void, and the renderer casts no shadow into a recess, so all three take the
 * niche colour.
 */
function niche(f: RunFaces, o: ArchOpening): void {
  const { builder, styles, frame, run } = f;
  const springing = f.section.crown - (o.to - o.from) / 2;
  const n = WALL_ARCADE.archSegments;
  const arc = Array.from({ length: n + 1 }, (_, k) => arcPoint(o, springing, k));
  const backOutline: Vec2[] = [[o.from, 0], [o.to, 0], ...arc.slice(0, n), [o.from, springing]];
  const back = backOutline.map((q) => point3(runAt(run, q[0], f.nicheBack), q[1]));
  orientedFace(builder, styles.niche, frame, back, planVector(run.across));
  for (const [s, sign] of [
    [o.from, 1],
    [o.to, -1],
  ] as const) {
    const side = [
      point3(runAt(run, s, f.nicheBack), 0),
      point3(runAt(run, s, f.front), 0),
      point3(runAt(run, s, f.front), springing),
      point3(runAt(run, s, f.nicheBack), springing),
    ];
    orientedFace(
      builder,
      styles.niche,
      frame,
      side,
      planVector([run.along[0] * sign, run.along[1] * sign])
    );
  }
  for (let k = 0; k < n; k++) {
    const p = arc[k]!;
    const q = arc[k + 1]!;
    const mid = (Math.PI * (k + 0.5)) / n;
    const soffit = [
      point3(runAt(run, p[0], f.nicheBack), p[1]),
      point3(runAt(run, q[0], f.nicheBack), q[1]),
      point3(runAt(run, q[0], f.front), q[1]),
      point3(runAt(run, p[0], f.front), p[1]),
    ];
    const inward = planVector(
      [-run.along[0] * Math.cos(mid), -run.along[1] * Math.cos(mid)],
      -Math.sin(mid)
    );
    orientedFace(builder, styles.niche, frame, soffit, inward);
  }
}

function fan(apex: Vec2, rim: readonly Vec2[]): Vec2[][] {
  return rim.slice(1).map((q, i) => [apex, rim[i]!, q]);
}

/*
 * Each half of the face fans from its top corner, which stands above and outside every point of its
 * half of the arch, so no triangle comes out a sliver. Ear-clipping the outline made one wherever a
 * bay measured 5.03 m: the chord beside the crown then pointed straight at a top corner.
 */
function archFace(
  s0: number,
  s1: number,
  walk: number,
  o: ArchOpening,
  springing: number
): Vec2[][] {
  const n = WALL_ARCADE.archSegments;
  const arc = Array.from({ length: n + 1 }, (_, k) => arcPoint(o, springing, k));
  const crown = arc[n / 2]!;
  const left: Vec2 = [s0, walk];
  const right: Vec2 = [s1, walk];
  const leftRim: Vec2[] = [[s0, 0], [o.from, 0], ...arc.slice(n / 2).reverse()];
  const rightRim: Vec2[] = [...arc.slice(0, n / 2 + 1).reverse(), [o.to, 0], [s1, 0]];
  return [...fan(left, leftRim), [left, crown, right], ...fan(right, rightRim)];
}

/**
 * The foot of one bay, under the shield wall and the two half piers and not under the niche,
 * which is open to the ground. `ends` are the bay's corners on the outer face and on the front,
 * at its start and at its end, measured along the run: at a bend they lie on the mitre.
 */
function footPiece(
  f: RunFaces,
  outer: number,
  ends: { readonly outer: readonly [number, number]; readonly front: readonly [number, number] },
  opening: ArchOpening | null
): void {
  const notch: Vec2[] = opening
    ? [
        [opening.to, f.front],
        [opening.to, f.nicheBack],
        [opening.from, f.nicheBack],
        [opening.from, f.front],
      ]
    : [];
  const outline: Vec2[] = [
    [ends.outer[0], outer],
    [ends.outer[1], outer],
    [ends.front[1], f.front],
    ...notch,
    [ends.front[0], f.front],
  ];
  for (const tri of triangulateRing(outline)) {
    const pts = tri.map((q) => point3(runAt(f.run, q[0], q[1]), 0));
    orientedFace(f.builder, f.styles.wall, f.frame, pts, [0, -1, 0]);
  }
}

/** One bay of the arcade face toward the city, from pier middle to pier middle, cut by its arch. */
function frontPiece(f: RunFaces, s0: number, s1: number, opening: ArchOpening | null): void {
  const { walk, crown } = f.section;
  const triangles = opening
    ? archFace(s0, s1, walk, opening, crown - (opening.to - opening.from) / 2)
    : triangulateRing([
        [s0, 0],
        [s1, 0],
        [s1, walk],
        [s0, walk],
      ]);
  const outward = planVector(f.run.across);
  for (const tri of triangles) {
    const pts = tri.map((q) => point3(runAt(f.run, q[0], f.front), q[1]));
    orientedFace(f.builder, f.styles.wall, f.frame, pts, outward);
  }
}

type ProfilePoint = readonly [number, number];

/**
 * Cross-section of a wall piece, counter-clockwise in (across toward the city, height), from its
 * foot: the foot, then with an arcade its front, the walk, the parapet and the outer face. The
 * foot is closed. A wall follows the ground along its length (mesh/drapedBuilder.ts), so its walk
 * slopes, and a sloping top is no closed solid without the foot under it.
 */
function wallProfile(
  thickness: number,
  height: number,
  section: WallSection | null
): ProfilePoint[] {
  const half = thickness / 2;
  if (!section)
    return [
      [-half, 0],
      [half, 0],
      [half, height],
      [-half, height],
    ];
  const front = half + section.depth;
  const parapet = -half + WALL_ARCADE.parapetThickness;
  return [
    [-half, 0],
    [front, 0],
    [front, section.walk],
    [parapet, section.walk],
    [parapet, height],
    [-half, height],
  ];
}

interface Sweep {
  readonly builder: MeshBuilder;
  readonly style: VertexStyle;
  readonly frame: Frame;
  readonly points: readonly Vec2[];
  readonly side: 1 | -1;
}

function acrossAt(points: readonly Vec2[], i: number, side: 1 | -1): Vec2 {
  const a = points[Math.min(i, points.length - 2)]!;
  const b = points[Math.min(i, points.length - 2) + 1]!;
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return [(-(b[1] - a[1]) / len) * side, ((b[0] - a[0]) / len) * side];
}

/** Every edge of the profile but those in `skip`, swept along the piece, and both end caps. */
function sweepProfile(
  sweep: Sweep,
  profile: readonly ProfilePoint[],
  skip: readonly number[]
): void {
  const { builder, style, frame, points, side } = sweep;
  const lines = new Map<number, Vec2[]>();
  const line = (a: number): Vec2[] => {
    const cached = lines.get(a) ?? offsetPolyline(points, side * a);
    lines.set(a, cached);
    return cached;
  };
  profile.forEach((p, k) => {
    const q = profile[(k + 1) % profile.length]!;
    if (skip.includes(k)) return;
    const [lp, lq] = [line(p[0]), line(q[0])];
    for (let i = 0; i < points.length - 1; i++) {
      const across = acrossAt(points, i, side);
      const quad = [
        point3(lp[i]!, p[1]),
        point3(lp[i + 1]!, p[1]),
        point3(lq[i + 1]!, q[1]),
        point3(lq[i]!, q[1]),
      ];
      const outward = planVector(
        [across[0] * (q[1] - p[1]), across[1] * (q[1] - p[1])],
        p[0] - q[0]
      );
      orientedFace(builder, style, frame, quad, outward);
    }
  });
  const last = points.length - 1;
  for (const [i, sign] of [
    [0, -1],
    [last, 1],
  ] as const) {
    const a = points[i === 0 ? 0 : last - 1]!;
    const b = points[i === 0 ? 1 : last]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const outward = planVector([((b[0] - a[0]) / len) * sign, ((b[1] - a[1]) / len) * sign]);
    for (const tri of triangulateRing(profile.map((p): Vec2 => [p[0], p[1]]))) {
      orientedFace(
        builder,
        style,
        frame,
        tri.map((q) => point3(line(q[0])[i]!, q[1])),
        outward
      );
    }
  }
}

function runOf(points: readonly Vec2[], i: number, side: 1 | -1): Run {
  const a = points[i]!;
  const b = points[i + 1]!;
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return {
    origin: a,
    along: [(b[0] - a[0]) / len, (b[1] - a[1]) / len],
    across: acrossAt(points, i, side),
  };
}

interface Bay {
  readonly segment: number;
  readonly opening: ArchOpening | null;
}

interface ArcadeLayout {
  /** The piece's path with a point under the middle of every pier between two arches. */
  readonly points: readonly Vec2[];
  /** One bay per span of `points`. */
  readonly bays: readonly Bay[];
}

/*
 * The arches of each straight run of the path, fitted between the mitres of the arcade front and of
 * the niche backs. Every face of the piece is cut under the middle of each pier, so no triangle
 * reaches along a whole run and every face meets its neighbours vertex to vertex.
 */
function layoutArcade(
  points: readonly Vec2[],
  side: 1 | -1,
  front: number,
  nicheBack: number
): ArcadeLayout {
  const frontLine = offsetPolyline(points, side * front);
  const nicheLine = offsetPolyline(points, side * nicheBack);
  const dense: Vec2[] = [points[0]!];
  const bays: Bay[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const run = runOf(points, i, side);
    const start = Math.max(coordinate(run, frontLine[i]!), coordinate(run, nicheLine[i]!));
    const end = Math.min(coordinate(run, frontLine[i + 1]!), coordinate(run, nicheLine[i + 1]!));
    const openings = archOpenings(end - start).map((o) => ({
      from: o.from + start,
      to: o.to + start,
    }));
    openings.slice(1).forEach((o, k) => dense.push(runAt(run, (openings[k]!.to + o.from) / 2, 0)));
    dense.push(points[i + 1]!);
    const count = Math.max(1, openings.length);
    for (let k = 0; k < count; k++) bays.push({ segment: i, opening: openings[k] ?? null });
  }
  return { points: dense, bays };
}

/**
 * A wall piece, one closed solid: the shield wall with its parapet on the moat side and, where the
 * wall is high enough, the walk and the arcade on the city side, a niche under every arch.
 */
export function buildWallPiece(
  builder: MeshBuilder,
  styles: WallStyles,
  frame: Frame,
  piece: WallPiece,
  section: WallSection | null
): void {
  const { points, thickness, height, side } = piece;
  const profile = wallProfile(thickness, height, section);
  if (!section) {
    sweepProfile({ builder, style: styles.wall, frame, points, side }, profile, []);
    return;
  }
  const half = thickness / 2;
  const front = half + section.depth;
  const nicheBack = half + WALL_ARCADE.lining;
  const layout = layoutArcade(points, side, front, nicheBack);
  // The foot and the front are built bay by bay, round the niches.
  sweepProfile(
    { builder, style: styles.wall, frame, points: layout.points, side },
    profile,
    [0, 1]
  );
  const frontLine = offsetPolyline(layout.points, side * front);
  const outerLine = offsetPolyline(layout.points, side * -half);
  layout.bays.forEach((bay, k) => {
    const run = runOf(points, bay.segment, side);
    const faces: RunFaces = { builder, styles, frame, run, section, nicheBack, front };
    const s0 = coordinate(run, frontLine[k]!);
    const s1 = coordinate(run, frontLine[k + 1]!);
    frontPiece(faces, s0, s1, bay.opening);
    footPiece(
      faces,
      -half,
      {
        outer: [coordinate(run, outerLine[k]!), coordinate(run, outerLine[k + 1]!)],
        front: [s0, s1],
      },
      bay.opening
    );
    if (bay.opening) niche(faces, bay.opening);
  });
}

/** Plan outline of a wall piece and its arcade. */
export function wallPieceRing(piece: WallPiece, section: WallSection | null): Ring {
  const depth = section?.depth ?? 0;
  const centre = offsetPolyline(piece.points, (piece.side * depth) / 2);
  return strokeRing(centre, piece.thickness / 2 + depth / 2);
}
